﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import type {
    IndividualIdentifierTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import type { OrganizationIdentifierSubTypes } from "~base/Internationalization/ConfigurationInfo";
import type { IdentifierParseTypes } from "~base/Types/Identifier";
import type * as sessionModel from "~base/Services/API/Session/Model";
import {
    getIdentifierTypesForModels,
    getSessionModelForSubjectTypes,
    sortSessionItemsDescending,
} from "~base/Pages/Home/Start/Functions";
import { getSubjectIdentifierConType } from "~base/Types/IdentifierConverter";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import type { CustomerStartPageTab } from "./Types";
import { CustomerStartPageSessionFilter } from "./Types";
import type { GetUserCustomerDomainResponse } from "~jrad/Services/API/Account/GetUserCustomerDomain";

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type SetLoading = { type: "setLoading" };
type SetSessionState = { type: "setSessionState"; state: SessionState };
type InitDataLoadedState = {
    type: "initDataLoaded";
    data: sessionModel.GetStartInfoResponse;
    subjectIdentifierFromQs: string;
    subjectIdentifierTypes: IdentifierParseTypes;
};
type SessionsForSubjectLoaded = {
    type: "sessionsForSubjectLoaded";
    subjectidentifier: string;
    data: sessionModel.SessionsResponse;
};
type setSubjectIdentifier = {
    type: "setSubjectIdentifier";
    value: string;
    parsedValue: IdentifierParseTypes;
};
type SetFetchError = { type: "fetchError"; message: string };
type CreateNewSession = { type: "createNewSession" };
type Navigate = {
    type: "navigate";
    uri: string;
    replace?: boolean;
    forceLoad?: boolean;
};
type PageRefresh = { type: "pageRefresh" };
type SetSelectedTab = { type: "setSelectedTab"; value: CustomerStartPageTab };
type SetPrevSessionPage = { type: "setPrevSessionPage"; page: number };
type SetPrevSessionFilter = { type: "setPrevSessionFilter"; value: string };
type SetUserCustomerDomain = {
    type: "setUserCustomerDomain";
    value: GetUserCustomerDomainResponse;
};

export type CustomerStartPageViewAction =
    | SetLoading
    | SetSessionState
    | InitDataLoadedState
    | SetFetchError
    | setSubjectIdentifier
    | SessionsForSubjectLoaded
    | CreateNewSession
    | Navigate
    | PageRefresh
    | SetSelectedTab
    | SetPrevSessionPage
    | SetPrevSessionFilter
    | SetUserCustomerDomain;

export interface CustomerStartPageState {
    selectedTab: CustomerStartPageTab;
    startInfo?: sessionModel.GetStartInfoResponse;
    subjectSessions?: Array<sessionModel.Session>;
    loadedSessionsForSubjectIdentifier?: string;
    selectedSessionModelLanguage?: string;
    selectedSessionModelId?: number;
    selectedOngoingSessionId?: number;
    selectedCompletedSessionId?: number;
    subjectIdentifier: string;
    subjectIdentifierType: string;
    subjectIdComesFromQS?: boolean;
    matchingSubjectTypes: Array<
        IndividualIdentifierTypes | OrganizationIdentifierTypes
    >;
    errorMessage?: string;
    loading: boolean;
    navigateUri?: string;
    navigateReplace?: boolean;
    navigateForceLoad?: boolean;
    hasLoadedTextCodes?: boolean;

    prevSessionPage: number;
    prevSessionFilter: string;

    userCustomerDomain?: GetUserCustomerDomainResponse;
}

export type ReducerContext = { intl: IntlContext };
export type SessionState = "New" | "Ongoing" | "Completed";

export function reducer(
    state: CustomerStartPageState,
    action: CustomerStartPageViewAction,
    context: ReducerContext
): CustomerStartPageState {
    switch (action.type) {
        case "setLoading": {
            return {
                ...state,
                errorMessage: null,
                loading: true,
            };
        }
        case "initDataLoaded": {
            const subjectId = action.data.RestrictToSubject
                ? action.data.RestrictedSubjectIdentifier
                : action.subjectIdentifierFromQs;
            const matchingSubjectTypes = action.data.RestrictToSubject
                ? getIdentifierTypesForModels([
                      action.data.RestrictedSubjectIdentifierType,
                  ]).flat()
                : action.subjectIdentifierTypes?.matchingTypes;
            const subjectIdentifierType = action.data.RestrictToSubject
                ? action.data.RestrictedSubjectIdentifierType
                : null;

            return {
                ...state,
                startInfo: action.data,
                loading: false,
                subjectIdentifier: subjectId ?? state.subjectIdentifier,
                subjectIdentifierType: subjectIdentifierType,
                subjectIdComesFromQS: subjectId ? true : false,
                matchingSubjectTypes:
                    matchingSubjectTypes ?? state.matchingSubjectTypes,
            };
        }
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "sessionsForSubjectLoaded": {
            return {
                ...state,
                loadedSessionsForSubjectIdentifier: action.subjectidentifier,
                subjectSessions: action.data.items,
                selectedOngoingSessionId: action.data.items
                    .sort(sortSessionItemsDescending)
                    .find((x) => x.state == "Active")?.id,
                selectedCompletedSessionId: action.data.items
                    .sort(sortSessionItemsDescending)
                    .find((x) => x.state == "Completed")?.id,
                errorMessage: null,
            };
        }
        case "navigate": {
            return {
                ...state,
                errorMessage: null,
                loading: action.uri ? true : false,
                navigateUri: action.uri,
                navigateReplace: action.replace,
                navigateForceLoad: action.forceLoad,
            };
        }
        case "pageRefresh": {
            if (state.navigateUri) {
                return {
                    ...state,
                    loading: false,
                    navigateUri: null,
                    navigateForceLoad: null,
                };
            }
            return state;
        }
        case "setSelectedTab": {
            if (window.location.pathname === "/Home/Start/Settings") {
                history.replaceState(null, null, "/Home/Start");
            }

            return {
                ...state,
                selectedTab: action.value,
            };
        }
        case "setPrevSessionPage": {
            return {
                ...state,
                prevSessionPage: action.page,
            };
        }
        case "setPrevSessionFilter": {
            return {
                ...state,
                prevSessionFilter: action.value,
                prevSessionPage: 1,
            };
        }
        case "setUserCustomerDomain": {
            return {
                ...state,
                userCustomerDomain: action.value,
            };
        }
    }
}
